/**
 * Meshy text-to-3D adapter.
 *
 * Real upstream calls — replaces the prior hard-coded prism stub. Uses the
 * BYOK proxy so the user's key is attached at the edge and never lands in
 * the client bundle or our logs.
 *
 * Flow:
 *   generate() POSTs a preview task → returns { status: 'queued', taskId }
 *   poll()     GETs the task; on SUCCEEDED returns the .glb URL.
 *
 * Endpoint: https://api.meshy.ai/openapi/v2/text-to-3d
 * Docs:     https://docs.meshy.ai/api-text-to-3d-beta
 */
import type { ProviderAdapter, ModelDescriptor, GenerateRequest, GenerateResult } from '../router/types';
import { proxy, ok, queued, fail } from './common';

const BASE = 'https://api.meshy.ai/openapi/v2/text-to-3d';

export const MESHY_MODELS: ModelDescriptor[] = [
  {
    id: 'meshy-3d',
    label: 'Meshy text-to-3D',
    mode: '3d',
    caps: {},
  },
];

interface MeshyTask {
  id: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'SUCCEEDED' | 'FAILED' | 'CANCELED' | 'EXPIRED';
  progress?: number;
  model_urls?: { glb?: string; fbx?: string; usdz?: string; obj?: string };
  task_error?: { message?: string };
}

export const meshy: ProviderAdapter = {
  id: 'meshy',
  models: MESHY_MODELS,

  async generate(modelId: string, req: GenerateRequest, apiKey: string): Promise<GenerateResult> {
    if (modelId !== 'meshy-3d') return fail(`Unknown model for Meshy: ${modelId}`);
    if (!apiKey) return fail('Meshy key missing — add it in API keys');

    try {
      const create = await proxy<{ result: string }>({
        provider: 'meshy',
        endpoint: BASE,
        method: 'POST',
        body: {
          mode: 'preview',
          prompt: req.prompt,
          art_style: (req.providerOpts?.artStyle as string) ?? 'realistic',
          negative_prompt: (req.providerOpts?.negativePrompt as string) ?? '',
          ai_model: (req.providerOpts?.aiModel as string) ?? 'meshy-4',
          topology: (req.providerOpts?.topology as string) ?? 'triangle',
          target_polycount: Number(req.providerOpts?.targetPolycount ?? 30000),
        },
        apiKey,
        authStyle: 'bearer',
      });
      return queued(create.result, create);
    } catch (e: any) {
      return fail(e?.message ?? 'meshy generate failed', e);
    }
  },

  async poll(taskId: string, apiKey: string): Promise<GenerateResult> {
    try {
      const r = await proxy<MeshyTask>({
        provider: 'meshy',
        endpoint: `${BASE}/${taskId}`,
        method: 'GET',
        apiKey,
        authStyle: 'bearer',
      });
      if (r.status === 'SUCCEEDED') {
        const url = r.model_urls?.glb;
        if (!url) return fail('Meshy succeeded but returned no .glb URL', r);
        return ok([{ url, mime: 'model/gltf-binary' }]);
      }
      if (r.status === 'FAILED' || r.status === 'CANCELED' || r.status === 'EXPIRED') {
        return fail(r.task_error?.message ?? `Meshy task ${r.status.toLowerCase()}`, r);
      }
      return queued(taskId, r);
    } catch (e: any) {
      return fail(e?.message ?? 'meshy poll failed', e);
    }
  },
};